import { Component, OnDestroy, OnInit, ViewChild, ViewEncapsulation } from '@angular/core';
import { forkJoin, fromEvent, Subject } from 'rxjs';
import { ActivatedRoute, NavigationEnd, NavigationStart, Router } from '@angular/router';
import { SidenavService } from '../sidenav/sidenav.service';
import { filter, switchMap, takeUntil, tap } from 'rxjs/operators';
import { DropdownService } from '../../services/dropdown.service';
import { CreateDeviceComponent } from '../../modules/global-add/create-device/create-device.component';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';
import { IdentityService } from '../../services/identity.service';
import { AddLayerComponent } from '../../modules/global-add/add-layer/add-layer.component';
import { AddDataSourceComponent } from '../../modules/global-add/add-data-source/add-data-source.component';
import { TeamsService } from '../../modules/teams/services/teams.service';
import { PermissionType } from '../../shared/enums/permission-type.enum';
import { TeamInterface } from '../../modules/teams/models/team.interface';
import { AddCustomMapComponent } from '../../modules/components/custom-maps/add-custom-map/add-custom-map.component';
import { TeamQrCodeFormComponent } from '../../modules/components/team-qr-codes/team-qr-code-form/team-qr-code-form.component';
import { TeamQrCodeModalData } from '../../modules/components/team-qr-codes/models/team-qr-code-modal-data';
import { CreateUserModalComponent } from '../../modules/users/create-user-modal/create-user-modal.component';
import { UserModalData } from '../../modules/users/models/user-modal-data';
import { FindUserModalComponent } from '../../modules/users/find-user-modal/find-user-modal.component';
import { User } from '../../modules/users/models/user';
import { MatDrawerMode } from '@angular/material/sidenav';
import { BreakpointObserver } from '@angular/cdk/layout';
import { AddEditCustomElevationComponent } from '../../modules/components/add-edit-custom-elevation/add-edit-custom-elevation.component';
import { MatIconRegistry } from '@angular/material/icon';
import { DomSanitizer } from '@angular/platform-browser';
import { AddProjectComponent } from '../../modules/global-add/add-project/add-project.component';
import { CompaniesService } from '../../modules/companies/companies.service';
import { AddProjectData } from '../../modules/global-add/add-project/add-project-data.interface';
import { ReportCategoriesService } from '../../modules/components/report-categories/report-categories.service';
import { UserRole } from '../../interfaces/user-role';
import { TranslationHandleService } from '../translate/translation-handle.service';
import { SidenavItemsTypes } from '../sidenav/sidenav-item/sidenav-item.model';
import { LangChangeEvent, TranslateService } from '@ngx-translate/core';
import { ZendeskService } from '../../services/zendesk.service';

const PHRASES_FOR_TRANSLATE = [
  {
    dashboard: 'sidebar.dashboard',
  },
  {
    userReports: 'sidebar.userReports',
  },
  {
    issueReports: 'sidebar.fieldReports',
  },
  {
    dataCollection: 'sidebar.dataCollection',
  },
  {
    realityCapture: 'sidebar.realityCapture',
  },
  {
    companies: 'sidebar.companies',
  },
  {
    company: 'sidebar.company',
  },
  {
    teams: 'sidebar.teams',
  },
  {
    team: 'sidebar.team',
  },
  {
    devices: 'sidebar.devices',
  },
  {
    dataSources: 'sidebar.dataSources',
  },
  {
    reports: 'sidebar.reports',
  },
  {
    help: 'sidebar.help',
  },
  {
    myProfile: 'sidebar.myProfile',
  },
  {
    components: 'sidebar.components',
  },
  {
    users: 'sidebar.users',
  },
  {
    pointClouds: 'PointClouds.Title',
  },
];

@Component({
  selector: 'mee-admin',
  templateUrl: './admin.component.html',
  styleUrls: ['./admin.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class AdminComponent implements OnInit, OnDestroy {
  @ViewChild('sidenav', { static: true }) sidenav;

  @ViewChild('scrollContainer', { static: true }) scrollContainer;
  isDropdownOpened = false;
  destroy$ = new Subject<void>();
  sidenavOpen = true;
  sidenavMode: MatDrawerMode = 'side';
  isMobile = false;
  outletState = 'hidden';
  dropdownTrigger = '#dropdown-trigger';
  translationArray: Map<string, string>;

  private isCreatePermissionsAllowed: boolean;

  get isSuperAdmin(): boolean {
    return this.identityService.isSuperAdmin();
  }

  get isCompanyAdmin(): boolean {
    return this.identityService.isCompanyAdmin();
  }

  get isTeamAdmin(): boolean {
    return this.identityService.isTeamAdmin();
  }

  get isShowDtmCreateButton() {
    if (this.isSuperAdmin || this.isCompanyAdmin) {
      return true;
    }

    return this.isCreatePermissionsAllowed;
  }

  get isShowMapCreateButton() {
    if (this.isSuperAdmin || this.isCompanyAdmin) {
      return true;
    }

    return this.isCreatePermissionsAllowed;
  }

  get isShowLayerCreateButton() {
    if (this.isSuperAdmin || this.isCompanyAdmin) {
      return true;
    }

    return this.isCreatePermissionsAllowed;
  }

  get isShowDataSourceCreateButton() {
    if (this.isSuperAdmin || this.isCompanyAdmin) {
      return true;
    }

    return this.isCreatePermissionsAllowed;
  }

  get isShowProjectCreateButton() {
    const isProjectCreator = Boolean(this.identityService.identity.Roles.find(role => role.Name === UserRole.PROJECT_CREATOR));

    return this.isSuperAdmin || this.isCompanyAdmin || isProjectCreator;
  }

  constructor(
    private router: Router,
    private breakpointObserver: BreakpointObserver,
    private sidenavService: SidenavService,
    private teamsService: TeamsService,
    private activatedRoute: ActivatedRoute,
    private readonly dropdownService: DropdownService,
    private readonly matDialog: MatDialog,
    private readonly identityService: IdentityService,
    private readonly matIconRegistry: MatIconRegistry,
    private readonly domSanitizer: DomSanitizer,
    private readonly companiesService: CompaniesService,
    private readonly reportCategoriesService: ReportCategoriesService,
    private readonly translationService: TranslationHandleService,
    private readonly zendeskService: ZendeskService,
    private readonly translate: TranslateService,
  ) {
    const self = this;
    this.translationService.setPhrasesForTranslate(PHRASES_FOR_TRANSLATE);
    this.translationService.getTranslationMap().subscribe(res => (this.translationArray = res));
    this.initSidenav();

    this.router.events
      .pipe(
        takeUntil(this.destroy$),
        tap(event => {
          if (event instanceof NavigationStart) {
            if (event.url !== router.url) {
              self.outletState = 'hidden';
            }
          }
        }),
      )
      .subscribe();

    this.sidenavService.onOpen
      .pipe(
        takeUntil(this.destroy$),
        tap(() => this.sidenav.open()),
      )
      .subscribe();

    this.sidenavService.onClose
      .pipe(
        takeUntil(this.destroy$),
        tap(() => this.sidenav.close()),
      )
      .subscribe();

    this.sidenavService.onToggle
      .pipe(
        takeUntil(this.destroy$),
        tap((opened: boolean) => {
          this.sidenavOpen = opened;
          if (this.sidenavOpen) {
            this.sidenav.open();
          } else {
            this.sidenav.close();
          }
        }),
      )
      .subscribe();

    this.sidenavService.mode.subscribe(mode => (this.sidenavMode = mode));
    this.dropdownService.isOpened$
      .pipe(
        tap(isOpened => (this.isDropdownOpened = isOpened)),
        takeUntil(this.destroy$),
      )
      .subscribe();

    this.matIconRegistry.addSvgIcon('project-icon', this.domSanitizer.bypassSecurityTrustResourceUrl('/assets/img/icons/project-icon.svg'));
  }

  ngOnInit(): void {
    this.translate.onLangChange
      .pipe(
        tap((langChangeEvent: LangChangeEvent) => {
          this.zendeskService.setWidgetLanguage(langChangeEvent.lang);
          this.translationService.setPhrasesForTranslate(PHRASES_FOR_TRANSLATE);
          this.initSidenav();
        }),
      )
      .subscribe();

    this.breakpointObserver
      .observe(['(max-width: 960px)'])
      .pipe(
        tap(result => {
          this.isMobile = result.matches;
          this.sidenavMode = this.isMobile ? 'over' : 'side';
          this.sidenavOpen = !this.isMobile;
        }),
        takeUntil(this.destroy$),
      )
      .subscribe();

    this.router.events
      .pipe(
        filter(event => event instanceof NavigationEnd),
        takeUntil(this.destroy$),
        tap(() => {
          this.scrollContainer.nativeElement.scrollTop = 0;
          if (this.isMobile) {
            this.sidenav.close();
          }
        }),
      )
      .subscribe();

    fromEvent(window, 'resize')
      .pipe(
        tap(() => {
          if (this.sidenavOpen && !this.sidenav.opened) {
            this.sidenav.open();
          }
        }),
      )
      .subscribe();

    if (this.isTeamAdmin) {
      this.setTeamPermissions();
    }
  }

  initSidenav() {
    this.sidenavService.clear();

    if (!this.identityService.isTeamAdmin()) {
      this.sidenavService.addItem(this.translationArray.get('company'), 'domain', '/company', 6);
    }

    /**
     * Common pages
     */
    this.sidenavService.addItem(this.translationArray.get('dashboard'), 'dashboard', '/dashboard', 1);
    // TODO: Temporarily hidden
    // this.sidenavService.addItem('Forms', 'message', '/forms', 2);
    // TODO: Not Implemented yet
    // this.sidenavService.addItem('Sessions', 'timelapse', '', 3);
    // this.sidenavService.addItem(this.translationArray.get('userReports'), 'photo_camera', '/reports', 4);

    this.sidenavService.addItem(this.translationArray.get('issueReports'), 'photo_camera', '/issue-reports', 2);

    this.sidenavService.addItem(this.translationArray.get('dataCollection'), 'place', '/data-collection', 3);

    this.sidenavService.addItem(this.translationArray.get('realityCapture'), '', '/reality-capture', 4, 'reality-capture-icon');

    this.sidenavService.addItem(this.translationArray.get('reports'), 'assignment_turned_in', '/system-reports', 5);
    // TODO: Not Implemented yet
    // this.sidenavService.addItem('System Audit', '', '', 5);

    this.sidenavService.addSeparator(5);

    if (this.identityService.isTeamAdmin()) {
      this.sidenavService.addItem(this.translationArray.get('team'), '', '/team', 6, 'project-icon');
    } else {
      this.sidenavService.addItem(this.translationArray.get('teams'), '', '/teams', 6, 'project-icon');
    }

    this.sidenavService.addItem(this.translationArray.get('users'), 'person', '/users', 7);
    this.sidenavService.addItem(this.translationArray.get('components'), '', '/components', 8, 'grid-item-add');
    this.sidenavService.addItem(this.translationArray.get('pointClouds'), '', '/point-clouds', 9, 'point-cloud');

    this.sidenavService.addItem(this.translationArray.get('dataSources'), 'settings_ethernet', '/sources', 10);

    this.sidenavService.addSeparator(10);
    this.sidenavService.addItem(
      this.translationArray.get('help'),
      'help',
      '/help',
      11,
      '',
      SidenavItemsTypes.Link,
      'https://support.vgis.io/',
    );
    this.sidenavService.addItem(this.translationArray.get('myProfile'), 'account_circle', '/profile', 12);
  }

  onActivate(event: any, scrollContainer: any): void {
    scrollContainer.scrollTop = 0;
    this.outletState = 'shown';
  }

  createDevice(event: any) {
    event.preventDefault();
    this.close(event);
    this.matDialog.open(CreateDeviceComponent, {
      width: '392px',
      panelClass: 'create-device',
    });
  }

  createUser(event: any) {
    event.preventDefault();
    this.close(event);

    this.matDialog
      .open<FindUserModalComponent, null, UserModalData>(FindUserModalComponent, {
        width: '392px',
        height: '272px',
      })
      .afterClosed()
      .pipe(
        filter(value => Boolean(value)),
        switchMap(data => {
          return this.matDialog
            .open<CreateUserModalComponent, UserModalData, User>(CreateUserModalComponent, {
              panelClass: 'create-user',
              data,
            })
            .afterClosed();
        }),
        takeUntil(this.destroy$),
      )
      .subscribe();
  }

  createProject(event: any) {
    event.preventDefault();
    this.close(event);

    const company$ = this.identityService.isCompanyAdmin()
      ? this.companiesService.getMyCompany()
      : this.companiesService.getCompanyBySlug(this.identityService.companySlug);
    const reportCategories$ = this.reportCategoriesService.getReportCategoriesForTeam();

    forkJoin({ company: company$, reportCategories: reportCategories$ })
      .pipe(
        switchMap(({ company, reportCategories }) =>
          this.matDialog
            .open<AddProjectComponent, AddProjectData>(AddProjectComponent, {
              panelClass: 'add-project',
              data: {
                company,
                reportCategories,
              },
              width: '392px',
              maxHeight: '95vh',
            })
            .afterClosed(),
        ),
      )
      .subscribe();
  }

  createLayer(event: any) {
    event.preventDefault();
    this.close(event);
    this.matDialog.open(AddLayerComponent, {
      panelClass: 'create-layer',
    });
  }

  createBasemap(event: any) {
    event.preventDefault();
    this.close(event);
    this.matDialog.open(AddCustomMapComponent, {
      panelClass: 'custom-map-dialog',
      data: {},
    });
  }

  createDataSource(event: MouseEvent) {
    event.preventDefault();
    this.close(event);
    this.matDialog.open(AddDataSourceComponent, {
      panelClass: 'add-data-source',
    });
  }

  createQrCode(event: MouseEvent) {
    event.preventDefault();
    this.close(event);
    this.matDialog.open<TeamQrCodeFormComponent, TeamQrCodeModalData, null>(TeamQrCodeFormComponent, {
      autoFocus: false,
      data: {
        isUpdate: false,
        qrCode: null,
      },
    });
  }

  createDTM(event: MouseEvent) {
    event.preventDefault();
    this.close(event);
    this.matDialog.open(AddEditCustomElevationComponent, {
      data: {
        isCreateMode: true,
      },
      disableClose: true,
      autoFocus: false,
      panelClass: 'elevation-form',
    });
  }

  close(event: any) {
    event.stopPropagation();
    this.dropdownService.close();
  }

  closeOnMisclick(shouldNotBeClosed: boolean) {
    if (!shouldNotBeClosed) {
      this.dropdownService.close();
    }
  }

  private setTeamPermissions() {
    /*TODO deal with null static injector error in app-routing module for remove [1]*/

    const currentTeam = this.activatedRoute.snapshot.data[1] as TeamInterface;

    const createPermission = currentTeam.Permissions.find(permission => permission.Type === PermissionType.Create);

    this.isCreatePermissionsAllowed = createPermission.IsAllowed;
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
